import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CommunityEvent } from "@/types/rider";
import { EventCard } from "./EventCard";
import { WBHVehicleOption, DaySelection } from "@/pages/RiderEventSelectWeBelongHereMiami";

interface MobileEventSelectorWBHProps {
  event: CommunityEvent;
  eventSelected: boolean;
  selectedDay: DaySelection;
  selectedVehicleId: string | null;
  vehicleOptions: WBHVehicleOption[];
  dayDates: Record<DaySelection, string>;
  onEventSelect: () => void;
  onBackToEvent: () => void;
  onDaySelect: (day: DaySelection) => void;
  onVehicleSelect: (vehicleId: string) => void;
  onContinue: () => void;
  onManualEntry: () => void;
}

export const MobileEventSelectorWBH = ({
  event,
  eventSelected,
  selectedDay,
  selectedVehicleId,
  vehicleOptions,
  dayDates,
  onEventSelect,
  onBackToEvent,
  onDaySelect,
  onVehicleSelect,
  onContinue,
  onManualEntry,
}: MobileEventSelectorWBHProps) => {
  const navigate = useNavigate();
  const selectedVehicle = vehicleOptions.find(v => v.id === selectedVehicleId);

  return (
    <div className="md:hidden bg-white text-black pb-24 px-4">
      <div className="flex justify-between items-center mb-8 pt-4">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-black hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="mr-1 h-5 w-5" />
          <span>Book A Custom Trip</span>
        </button>
      </div>

      <h1 className="text-3xl font-bold text-center mb-4 text-black">Book Your Ride To</h1>
      
      <AnimatePresence mode="wait">
        {!eventSelected ? (
          // Event Selection View - Single Card
          <motion.div
            key="event-selection"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center"
          >
            <motion.div 
              className="w-full max-w-[330px] cursor-pointer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onEventSelect}
            >
              <EventCard
                event={event}
                isSelected={false}
                onClick={onEventSelect}
              />
            </motion.div>
            
            <motion.p 
              className="text-xl text-center mt-6 mb-4 text-black font-semibold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Tap To See Options
            </motion.p>
            
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-gray-600 mb-2">Or</p>
              <button 
                onClick={onManualEntry}
                className="text-gray-600 underline hover:text-black transition-colors"
              >
                Book A Custom Trip
              </button>
            </motion.div>
          </motion.div>
        ) : (
          // Vehicle Selection View
          <motion.div
            key="vehicle-selection"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col"
          >
            <button 
              onClick={onBackToEvent}
              className="flex items-center text-black hover:text-gray-700 transition-colors mb-4"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              <span className="text-sm">Back</span>
            </button>

            <h2 className="text-2xl font-bold mb-2 text-black">{event.name}</h2>
            
            {/* Day Selector */}
            <div className="mb-6">
              <p className="text-gray-600 mb-3">Select Your Day</p>
              <div className="flex gap-2">
                {(['friday', 'saturday', 'sunday'] as DaySelection[]).map((day) => (
                  <button
                    key={day}
                    onClick={() => onDaySelect(day)}
                    className={`flex-1 py-3 px-2 rounded-full text-sm font-medium transition-all ${
                      selectedDay === day
                        ? 'bg-black text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
                ))}
              </div>
              <p className="text-center text-gray-500 mt-2 text-sm">{dayDates[selectedDay]}</p>
            </div>

            {/* Vehicle Options */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-black">Choose Your Option</h3>
              <div className="space-y-3">
                {vehicleOptions.map((vehicle) => (
                  <motion.div
                    key={vehicle.id}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div 
                      className={`cursor-pointer transition-all duration-200 border rounded-xl p-4 ${
                        selectedVehicleId === vehicle.id
                          ? 'border-black bg-gray-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      onClick={() => onVehicleSelect(vehicle.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">{vehicle.icon}</div>
                          <div>
                            <h4 className="font-semibold text-black">{vehicle.name}</h4>
                            <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {vehicle.capacity === 1 ? '1 person' : `${vehicle.capacity} people`}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {vehicle.durationHours}hr
                              </span>
                            </div>
                            <span className={`inline-block mt-2 text-xs px-2 py-1 rounded-full ${
                              vehicle.isRoundTrip 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {vehicle.isRoundTrip ? 'Round Trip' : 'One Way'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-black">${vehicle.price}</div>
                          {vehicle.capacity > 1 && (
                            <p className="text-xs text-gray-500">${Math.round(vehicle.price / vehicle.capacity)}/person</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Continue Button */}
            {selectedVehicleId && selectedVehicle && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4"
              >
                <div className="flex justify-between items-center mb-4 border border-gray-200 rounded-lg p-4 bg-white">
                  <div>
                    <p className="text-2xl font-bold text-black">${selectedVehicle.price}</p>
                    <p className="text-gray-500 text-sm">Total Price</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-black">{dayDates[selectedDay]}</p>
                    <p className="text-gray-500 text-sm">{selectedVehicle.isRoundTrip ? 'Round Trip' : 'One Way'}</p>
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-black text-white hover:bg-gray-800 rounded-full py-6"
                  onClick={onContinue}
                >
                  Book Today 🚌
                </Button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};